import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Monitor, Users, TrendingUp, Shield, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const MSNService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "MSN Microsoft Network Advertising",
    "description": "Placements publicitaires premium sur l'écosystème Microsoft : Outlook, Bing Search, Edge Shopping, MSN Start. 400M+ utilisateurs actifs.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Premium Microsoft Users"
    }
  };

  const features = [
    {
      icon: Monitor,
      title: tx(currentLanguage, 'Outlook Desktop & Mobile', 'Outlook Desktop & Mobile', 'Outlook Desktop & Mobile'),
      description: tx(currentLanguage, 'Intégration native dans l\'écosystème Outlook avec plus de 400 millions d\'utilisateurs actifs', 'Native integration in Outlook ecosystem with 400+ million active users', 'Native Integration im Outlook-Ökosystem mit 400+ Millionen aktiven Nutzern')
    },
    {
      icon: Users,
      title: tx(currentLanguage, 'Bing Search & Shopping', 'Bing Search & Shopping', 'Bing Search & Shopping'),
      description: tx(currentLanguage, 'Placement premium sur Bing avec audiences haute intention d\'achat', 'Premium placement on Bing with high purchase intent audiences', 'Premium-Platzierung bei Bing mit Zielgruppen mit hoher Kaufabsicht')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'Edge Tiles & Shopping', 'Edge Tiles & Shopping', 'Edge Tiles & Shopping'),
      description: tx(currentLanguage, 'Visibilité optimale sur Microsoft Edge, le navigateur en croissance rapide', 'Optimal visibility on Microsoft Edge, the fast-growing browser', 'Optimale Sichtbarkeit in Microsoft Edge, dem schnell wachsenden Browser')
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: tx(currentLanguage, 'Environnement Brand-Safe', 'Brand-Safe Environment', 'Markensichere Umgebung'),
      description: tx(currentLanguage, 'Placements exclusivement sur les plateformes Microsoft de confiance', 'Placements exclusively on trusted Microsoft platforms', 'Platzierungen ausschließlich auf vertrauenswürdigen Microsoft-Plattformen')
    },
    {
      icon: Target,
      title: tx(currentLanguage, 'Audiences Premium', 'Premium Audiences', 'Premium-Zielgruppen'),
      description: tx(currentLanguage, 'Accès aux utilisateurs Microsoft avec pouvoir d\'achat élevé', 'Access to Microsoft users with high purchasing power', 'Zugang zu Microsoft-Nutzern mit hoher Kaufkraft')
    },
    {
      icon: Zap,
      title: tx(currentLanguage, 'Performance Optimisée', 'Optimized Performance', 'Optimierte Leistung'),
      description: tx(currentLanguage, 'Algorithmes Microsoft pour maximiser vos conversions', 'Microsoft algorithms to maximize your conversions', 'Microsoft-Algorithmen zur Maximierung Ihrer Conversions')
    }
  ];

  const stats = [
    { value: '400M+', label: tx(currentLanguage, 'Utilisateurs Outlook', 'Outlook Users', 'Outlook-Nutzer') },
    { value: '35%', label: tx(currentLanguage, 'Part de marché Bing', 'Bing Market Share', 'Bing-Marktanteil') },
    { value: '15%', label: tx(currentLanguage, 'CTR moyen', 'Average CTR', 'Durchschnittliche CTR') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "MSN Microsoft Network Advertising | Outlook, Bing, Edge Ads - OptimHub")}
        description={tr(currentLanguage, "Publicité premium sur l'écosystème Microsoft : Outlook (400M+ users), Bing Search, Edge Shopping, MSN Start. Content commerce dans environnement brand-safe. CPC vers CPA.")}
        keywords="MSN advertising, Microsoft network ads, Outlook advertising, Bing ads, Edge browser ads, Microsoft ecosystem, premium advertising, content commerce Microsoft, brand safe advertising"
        ogImage="https://images.unsplash.com/photo-1533069027836-fa937181a8ce?w=1200"
        routeKey="serviceMsn"
        lang={currentLanguage}
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Solution Microsoft', 'Microsoft Solution', 'Microsoft-Lösung')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                MSN Microsoft Network
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Accédez à l\'écosystème Microsoft avec des placements sur Outlook, Bing Search, Edge Shopping et MSN Start. Touchez des audiences qualifiées sur des plateformes de confiance mondiale.', 'Access the Microsoft ecosystem with placements on Outlook, Bing Search, Edge Shopping and MSN Start. Reach qualified audiences on globally trusted platforms.', 'Zugang zum Microsoft-Ökosystem mit Platzierungen auf Outlook, Bing Search, Edge Shopping und MSN Start. Erreichen Sie qualifizierte Zielgruppen auf weltweit vertrauenswürdigen Plattformen.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo MSN Microsoft Network"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1533069027836-fa937181a8ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxNaWNyb3NvZnQlMjBhZHZlcnRpc2luZ3xlbnwwfHx8Ymx1ZXwxNzYxNTc3Mjg5fDA&ixlib=rb-4.1.0&q=85"
                alt="MSN Microsoft Network Advertising"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Placements Premium Microsoft', 'Premium Microsoft Placements', 'Premium Microsoft-Platzierungen')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Maximisez votre visibilité avec des placements natifs dans l\'écosystème Microsoft le plus utilisé au monde.', 'Maximize your visibility with native placements in the world\'s most used Microsoft ecosystem.', 'Maximieren Sie Ihre Sichtbarkeit mit nativen Platzierungen im weltweit meistgenutzten Microsoft-Ökosystem.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi Choisir MSN ?', 'Why Choose MSN?', 'Warum MSN wählen?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Maximiser Votre ROI ?', 'Ready to Maximize Your ROI?', 'Bereit, Ihren ROI zu maximieren?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Rejoignez des milliers d\'annonceurs qui font confiance à OptimHub pour leurs campagnes Microsoft Network.', 'Join thousands of advertisers who trust OptimHub for their Microsoft Network campaigns.', 'Schließen Sie sich Tausenden von Werbetreibenden an, die OptimHub für ihre Microsoft Network-Kampagnen vertrauen.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </button>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default MSNService;
